/**
 * The document review queue (S-A44).
 *
 * ===========================================================================
 * THE TWO RULES THAT MATTER HERE
 * ===========================================================================
 * 1. A REJECTION CARRIES TWO REASONS, TO TWO AUDIENCES. `client_reason` is the only
 *    wording a client may ever see; `internal_reason` is staff-only and is
 *    structurally absent from every portal payload. The composer below puts them in
 *    visually unmistakable containers, because getting them the wrong way round is
 *    the CR-25 leak class in its purest form.
 *
 * 2. SCANNING IS A GATE, NOT A LABEL. `is_downloadable` is derived at read time from
 *    the current version's scan status, so a quarantined file cannot be opened however
 *    the rest of the record looks. Read that field; do not re-derive it.
 * ===========================================================================
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  usePaginatedList,
  type Document,
  type DocumentListItem,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Drawer,
  Modal,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '@legalcrm/ui';
import { useEffect, useState } from 'react';

import { Can } from '@/auth/permissions';
import { formatDateTime, humanise } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';

import { DeleteDocumentDialog } from './DeleteDocumentDialog';
import { DocumentsTable } from './DocumentsTable';

const STATUS_OPTIONS = [
  { value: 'under_review', label: 'Awaiting review' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: '', label: 'All documents' },
];

export function DocumentReviewPage() {
  const [status, setStatus] = useState('under_review');
  const [openId, setOpenId] = useState<string | null>(null);

  const list = usePaginatedList<DocumentListItem>(qk.list('documents'), STAFF.documents, {
    status: status || undefined,
  });

  const { resetCursor } = list;
  useEffect(() => {
    resetCursor();
  }, [status, resetCursor]);

  return (
    <>
      <PageHeader
        title="Document review"
        description="What clients have sent in, waiting for someone to look at it."
      />

      <div className="mb-3 max-w-xs">
        <Select
          label="Status"
          hideLabel
          options={STATUS_OPTIONS}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        />
      </div>

      <DocumentsTable
        rows={list.items}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={(row) => setOpenId(row.id)}
        emptyTitle="Nothing to review"
        emptyDescription="No documents match this filter."
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
          summary: `${list.items.length} shown`,
        }}
      />

      {openId && <ReviewPanel documentId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

export function ReviewPanel({ documentId, onClose }: { documentId: string; onClose: () => void }) {
  const api = useApi();
  const { toast } = useToast();
  const [rejecting, setRejecting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [opening, setOpening] = useState(false);

  /**
   * The download endpoint needs the staff bearer token, which a plain navigation
   * never carries -- opening the API URL in a tab was refused, and the refusal
   * surfaced as a server error. So the request is made here, signed in, and its
   * answer opened: a short-lived link for ordinary documents, or the file itself
   * for high-sensitivity ones, which are streamed rather than linked.
   *
   * The tab is opened before the request so the browser treats it as the user's
   * click rather than blocking it as a pop-up.
   */
  async function openDocument(id: string) {
    const tab = window.open('', '_blank');
    setOpening(true);
    try {
      const blob = await api.get<Blob>(STAFF.documentDownload(id), { asBlob: true });
      let target: string;
      if (blob.type.includes('json')) {
        const body = JSON.parse(await blob.text()) as { url: string };
        target = body.url;
      } else {
        target = URL.createObjectURL(blob);
        setTimeout(() => URL.revokeObjectURL(target), 60_000);
      }
      if (tab) {
        tab.opener = null;
        tab.location.href = target;
      } else {
        window.location.assign(target);
      }
    } catch (error) {
      tab?.close();
      toast({
        title: 'Could not open the document',
        description: error instanceof Error ? error.message : 'Please try again.',
        tone: 'warning',
      });
    } finally {
      setOpening(false);
    }
  }

  const query = useApiQuery<Document>(
    qk.detail('documents', documentId),
    STAFF.document(documentId),
  );

  const approve = useApiMutation<Document, void>(
    {
      mutationFn: () =>
        api.post<Document>(STAFF.documentReview(documentId), { decision: 'approved' }),
      invalidates: [qk.all('documents')],
    },
    {
      onSuccess: () => {
        toast({ title: 'Document approved', tone: 'success' });
        onClose();
      },
      onError: (error) =>
        toast({
          title: 'Could not approve',
          description: error instanceof ConflictError ? error.detail : 'Try again.',
          tone: 'warning',
        }),
    },
  );

  /**
   * Erases the file and keeps the record. Everything that counts documents is
   * invalidated with it: the lists, a matter's outstanding badge (a request awaiting
   * this file goes back to the client) and the dashboard's review queue.
   */
  const remove = useApiMutation<unknown, string>(
    {
      mutationFn: (reason) => api.delete(STAFF.document(documentId), { body: { reason } }),
      invalidates: [
        qk.all('documents'),
        qk.all('document-requests'),
        qk.all('cases'),
        qk.dashboard(),
      ],
    },
    {
      onSuccess: () => {
        toast({ title: 'Document deleted', tone: 'success' });
        setDeleting(false);
        onClose();
      },
    },
  );

  const document = query.data;

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        title={document?.name ?? 'Document'}
        description={document ? humanise(document.kind) : undefined}
        size="lg"
        footer={
          document ? (
            <>
              {/* Capability rather than `_actions`, as for matters and clients: an
                  administrator and partner grant. The server re-checks scope. */}
              <Can module="document" action="delete">
                <Button variant="ghost" onClick={() => setDeleting(true)}>
                  Delete document
                </Button>
              </Can>
              {document.status !== 'approved' && (
                <>
                  <Can module="document" action="reject">
                    <Button variant="secondary" onClick={() => setRejecting(true)}>
                      Reject
                    </Button>
                  </Can>
                  <Can module="document" action="approve">
                    <Button
                      variant="primary"
                      loading={approve.isPending}
                      onClick={() => approve.mutate()}
                    >
                      Approve
                    </Button>
                  </Can>
                </>
              )}
            </>
          ) : null
        }
      >
        {query.isLoading && <Spinner label="Loading document" />}

        {document && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-2">
              <Badge tone={document.status === 'approved' ? 'success' : 'info'}>
                {humanise(document.status)}
              </Badge>
              <Badge
                tone={document.visibility === 'internal' ? 'internal' : 'client-facing'}
              >
                {document.visibility === 'internal'
                  ? 'Internal only'
                  : 'Client can see this'}
              </Badge>
              <Badge tone={document.scan_status === 'clean' ? 'success' : 'warning'} dot>
                {humanise(document.scan_status)}
              </Badge>
            </div>

            {/*
              The gate, stated plainly. `is_downloadable` is the server's answer,
              derived from the current version at read time — not something to
              recompute from `scan_status` here.
            */}
            {!document.is_downloadable ? (
              <div className="rounded border border-warning/30 bg-warning-subtle px-3 py-2 text-sm text-ink">
                <strong className="font-semibold">Not available to open.</strong> This file
                has not cleared malware scanning, so it cannot be downloaded by anyone —
                including the firm.
              </div>
            ) : (
              <Button
                variant="secondary"
                loading={opening}
                onClick={() => void openDocument(document.id)}
              >
                Open document
              </Button>
            )}

            <dl className="flex flex-col gap-2 border-t pt-4">
              <Row label="Versions">{document.version_count}</Row>
              <Row label="Added">{formatDateTime(document.created_at)}</Row>
              <Row label="Last updated">{formatDateTime(document.updated_at)}</Row>
            </dl>
          </div>
        )}
      </Drawer>

      {document && (
        <RejectDocumentDialog
          open={rejecting}
          onClose={() => setRejecting(false)}
          documentId={document.id}
          onDone={onClose}
        />
      )}

      {document && (
        <DeleteDocumentDialog
          open={deleting}
          onClose={() => setDeleting(false)}
          name={document.name}
          approved={document.status === 'approved'}
          linkedToRequest={Boolean(document.request)}
          busy={remove.isPending}
          error={remove.error}
          onConfirm={(reason) => remove.mutateAsync(reason)}
        />
      )}
    </>
  );
}

function RejectDocumentDialog({
  open,
  onClose,
  documentId,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  documentId: string;
  onDone: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const [clientReason, setClientReason] = useState('');
  const [internalReason, setInternalReason] = useState('');

  const reject = useApiMutation<
    Document,
    { decision: string; client_reason: string; internal_reason: string }
  >(
    {
      mutationFn: (body) => api.post<Document>(STAFF.documentReview(documentId), body),
      invalidates: [qk.all('documents')],
    },
    {
      onSuccess: () => {
        toast({
          title: 'Document rejected',
          description: 'The client has been asked to send a replacement.',
          tone: 'success',
        });
        setClientReason('');
        setInternalReason('');
        onClose();
        onDone();
      },
      onError: (error) =>
        toast({
          title: 'Could not reject',
          description: error instanceof ConflictError ? error.detail : 'Try again.',
          tone: 'warning',
        }),
    },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reject this document"
      description="Tell the client what was wrong and what to send instead."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={reject.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!clientReason.trim()}
            loading={reject.isPending}
            onClick={() =>
              reject.mutate({
                decision: 'rejected',
                client_reason: clientReason.trim(),
                internal_reason: internalReason.trim(),
              })
            }
          >
            Reject document
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/*
          The two reasons are deliberately in separate, labelled containers with
          different colours AND different words. Colour alone is not sufficient —
          somebody rejecting forty documents on a Friday afternoon needs the
          distinction to survive skim-reading.
        */}
        <div className="rounded-lg border border-client-facing/40 bg-client-facing-subtle/40 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-client-facing">
            The client will read this
          </p>
          <Textarea
            label="Reason for the client"
            hint="What was wrong, and what to send instead. A rejection they cannot act on is worse than none."
            rows={3}
            required
            value={clientReason}
            onChange={(event) => setClientReason(event.target.value)}
          />
        </div>

        <div className="rounded-lg border border-internal/40 bg-internal-subtle/40 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-internal">
            Internal only — never shown to the client
          </p>
          <Textarea
            label="Internal note"
            hint="Optional. For the firm's own record."
            rows={2}
            value={internalReason}
            onChange={(event) => setInternalReason(event.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className="text-right text-base text-ink">{children}</dd>
    </div>
  );
}
